import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@/components/base/Alert';
import Button from '@/components/base/Button';
import Input from '@/components/base/Input';
import Modal from '@/components/base/Modal';
import { systemSettings } from '@/mocks/settings';
import { formatNumber } from '@/lib/format';
import { ITEM_SERVICE_TYPES, type ItemPayload, type ItemServiceType, type LaundryItem } from '@/types';
import ServicePriceSection from './ServicePriceSection';
import {
  buildServiceForm,
  toServiceRates,
  validateService,
  type ServiceFormMap,
} from './itemForm';

interface ItemFormModalProps {
  open: boolean;
  item: LaundryItem | null;
  onClose: () => void;
  onSubmit: (payload: ItemPayload, id: string | null) => Promise<void>;
}

export default function ItemFormModal({ open, item, onClose, onSubmit }: ItemFormModalProps) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [services, setServices] = useState<ServiceFormMap>(() => buildServiceForm());
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageCleared, setImageCleared] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Reset the form every time the modal opens (for add or for a specific item).
  useEffect(() => {
    if (!open) return;
    setName(item?.name ?? '');
    setServices(buildServiceForm(item?.services));
    setImageFile(null);
    setImageCleared(false);
    setImageError(null);
    setErrors({});
    setFormError(null);
    setSaving(false);
  }, [open, item]);

  // Object URL for the freshly picked file (cleaned up automatically).
  useEffect(() => {
    if (!imageFile) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(imageFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  const displayImage = previewUrl ?? (imageCleared ? null : item?.imageUrl ?? null);

  const resetFileInput = () => {
    if (fileRef.current) fileRef.current.value = '';
  };

  const onPickFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setImageError(null);
    if (!file) return;

    const accept = systemSettings.imageUpload.accept
      .split(',')
      .map((s) => s.trim().toLowerCase());
    const ext = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
    if (accept.length && !accept.includes(ext)) {
      setImageError(t('upload.wrongType'));
      resetFileInput();
      return;
    }
    if (file.size > systemSettings.imageUpload.maxSizeMB * 1024 * 1024) {
      setImageError(t('upload.tooLarge').replace('{size}', formatNumber(systemSettings.imageUpload.maxSizeMB)));
      resetFileInput();
      return;
    }
    setImageFile(file);
    setImageCleared(false);
  };

  const setService = (key: ItemServiceType, next: ServiceFormMap[ItemServiceType]) => {
    setServices((prev) => ({ ...prev, [key]: next }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (!name.trim()) nextErrors.name = t('items.required.name');
    Object.assign(nextErrors, validateService(services, t));

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setFormError(t('items.form.errorFix'));
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      const payload: ItemPayload = {
        name: name.trim(),
        imageUrl: imageFile ? previewUrl : imageCleared ? null : item?.imageUrl ?? null,
        services: toServiceRates(services),
      };
      await onSubmit(payload, item?.id ?? null);
      onClose();
    } catch {
      setFormError(t('items.errorSave'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={saving ? () => undefined : onClose}
      size="lg"
      fullScreen
      title={item ? t('items.edit.title') : t('items.add.title')}
      footer={
        <>
          <Button variant="outline" size="md" onClick={onClose} disabled={saving}>
            {t('items.form.cancel')}
          </Button>
          <Button
            type="submit"
            form="item-form"
            variant="primary"
            size="md"
            loading={saving}
          >
            {item ? t('items.form.saveEdit') : t('items.form.save')}
          </Button>
        </>
      }
    >
      <form id="item-form" className="space-y-5" onSubmit={handleSubmit} noValidate>
        {formError ? <Alert variant="error">{formError}</Alert> : null}

        <section className="space-y-4">
          <h4 className="text-sm font-semibold text-foreground-950">{t('items.form.itemData')}</h4>

          <Input
            id="item-name"
            name="name"
            label={t('items.form.name')}
            requiredMark
            value={name}
            error={errors.name}
            onChange={(e) => setName(e.target.value)}
          />

          <div>
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-foreground-800">
                {t('items.form.image')}
                <span className="ms-1 text-xs font-normal text-foreground-400">({t('items.image.optional')})</span>
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-md border border-background-300 bg-background-100">
                {displayImage ? (
                  <img src={displayImage} alt={name || t('items.form.image')} className="h-full w-full object-cover" />
                ) : (
                  <span className="flex flex-col items-center gap-1 text-foreground-400">
                    <i className="ri-image-line text-2xl leading-none" aria-hidden="true" />
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <input
                  ref={fileRef}
                  id="item-image"
                  name="image"
                  type="file"
                  accept={systemSettings.imageUpload.accept}
                  className="sr-only"
                  onChange={onPickFile}
                />
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    icon="ri-upload-2-line"
                    onClick={() => fileRef.current?.click()}
                  >
                    {displayImage && !imageFile ? t('items.image.replace') : t('items.image.choose')}
                  </Button>
                  {displayImage || imageFile ? (
                    <Button
                      variant="danger-outline"
                      size="sm"
                      icon="ri-delete-bin-line"
                      onClick={() => {
                        setImageFile(null);
                        setImageCleared(true);
                        setImageError(null);
                        resetFileInput();
                      }}
                    >
                      {t('items.image.remove')}
                    </Button>
                  ) : null}
                </div>
                <p className="mt-1.5 text-xs text-foreground-500">
                  {imageError ? <span className="text-rose-600">{imageError}</span> : t('items.image.hint')}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h4 className="text-sm font-semibold text-foreground-950">{t('items.form.services')}</h4>
            <span className="text-xs text-foreground-500">{t('items.form.serviceNote')}</span>
          </div>

          {ITEM_SERVICE_TYPES.map((key) => (
            <ServicePriceSection
              key={key}
              serviceKey={key}
              value={services[key]}
              onChange={(next) => setService(key, next)}
              errors={{ normal: errors[`${key}.normal`], urgent: errors[`${key}.urgent`] }}
            />
          ))}
        </section>
      </form>
    </Modal>
  );
}